import { z } from 'zod';

export { z };

export class ConfigError extends Error {
  readonly issues: string[];
  constructor(issues: string[]) {
    super(`Invalid configuration:\n  - ${issues.join('\n  - ')}`);
    this.issues = issues;
    this.name = 'ConfigError';
  }
}

/**
 * Parses and validates configuration from an environment-like record once, at startup.
 * Every service declares its whole configuration as one schema; nothing else reads process.env.
 */
export function loadConfig<S extends z.ZodType>(
  schema: S,
  env: Record<string, string | undefined> = process.env,
): z.infer<S> {
  // A plain copy: zod 4 accepts only a plain object as a record, and `process.env` is not one (an exotic object),
  // so `z.record(...)` over the environment refused it; the copy is also the snapshot read once at startup.
  const result = schema.safeParse({ ...withSecretFiles(env) });
  if (!result.success) {
    throw new ConfigError(
      result.error.issues.map((i) => `${i.path.map(String).join('.') || '(root)'}: ${i.message}`),
    );
  }
  return result.data;
}

const KEY = /^[A-Z][A-Z0-9_]*$/;

/**
 * Secrets as files (threat model T16): with `CONFIG_SECRETS_DIR=<dir>`, a key the environment does not set
 * is read from the file `<dir>/<KEY>`, so a pod mounts its Secret as a volume (one file per key) instead of
 * exposing it as environment variables (`/proc/<pid>/environ`, crash dumps, child processes). The
 * environment's own value wins; one trailing newline is dropped. There is no `<KEY>_FILE` form here: keys
 * such as `KMS_LOCAL_KEY_FILE` and `SECRETS_FILE` are already paths the services read themselves. Node
 * only (the browser never has the variable).
 */
export function withSecretFiles(env: Record<string, string | undefined>): Record<string, string | undefined> {
  const dir = env['CONFIG_SECRETS_DIR'];
  if (!dir) return env;
  const fs = process.getBuiltinModule('node:fs');
  const path = process.getBuiltinModule('node:path');
  const out = { ...env };
  for (const name of fs.readdirSync(dir)) {
    // A Secret volume also holds `..data` and timestamped dirs: only KEY-shaped names are values.
    if (!KEY.test(name) || out[name] !== undefined) continue;
    const file = path.join(dir, name);
    if (fs.statSync(file).isFile()) out[name] = fs.readFileSync(file, 'utf8').replace(/\r?\n$/, '');
  }
  return out;
}

/** Shared building blocks for service config schemas. */
export const common = {
  nodeEnv: z.enum(['development', 'test', 'production']).default('development'),
  port: z.coerce.number().int().min(1).max(65535),
  logLevel: z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal']).default('info'),
  url: z.url(),
  /**
   * PUBLIC_HOSTS: the hosts a service is served on, comma-separated (the chart sets it from the ingress's hosts),
   * lower-cased; empty when it has no ingress.
   */
  publicHosts: z
    .string()
    .default('')
    .transform((v) =>
      v
        .split(',')
        .map((h) => h.trim().toLowerCase())
        .filter(Boolean),
    )
    .pipe(
      z.array(
        z
          .string()
          .regex(
            /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/,
            'a host name',
          ),
      ),
    ),
};
